import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { daDate } from "@/lib/date";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FollowupCardCompact } from "@/components/le/FollowupCard";
import { ChevronDown, ChevronRight, Flag, Plus } from "lucide-react";
import { MStatusBadge } from "@/components/le/MinutesUI";

const NEUTRAL = "bg-[#F5F4F2] text-stone-600 border-[#EBE8E3]";
const FST_BADGE = { "Åben": NEUTRAL, "Afventer kontrol": NEUTRAL, "Lukket": "bg-[#E7EADF] text-[#5f6b4a] border-[#D6DCC8]" };
const EV_LABEL = { note: "Opfølgning", responsible: "Ansvarlig →", due: "Frist →", status: "Status →", closed: "Lukket" };

function FEvent({ f }) {
  return <p className="text-sm text-stone-700 whitespace-pre-wrap">{f.type && f.type !== "note" ? <span className="text-[11px] mr-1.5 px-1.5 py-0.5 rounded bg-stone-100 text-stone-500">{EV_LABEL[f.type] || f.type}</span> : null}{f.text}</p>;
}

function FindingFollowupDialog({ finding, onClose, onSaved }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!note.trim()) { toast.error("Skriv en opfølgning"); return; }
    setSaving(true);
    try { await api.post(`/audit/findings/${finding.id}/followup`, { note }); toast.success("Opfølgning gemt"); onSaved?.(); onClose(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-2xl max-w-lg w-[calc(100vw-1.5rem)] sm:w-full [&>*]:min-w-0" data-testid="finding-followup-dialog">
        <DialogHeader><DialogTitle>Opfølgning · {finding.finding_no}</DialogTitle></DialogHeader>
        <p className="text-sm text-stone-600">{finding.description}</p>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4} className="rounded-xl" placeholder="Hvad blev gjort · hvad blev målt · resultat" data-testid="finding-followup-note" />
        <p className="text-[11px] text-stone-500">Ændrer ikke fundets status, kategori eller lukning — det sker kun i audit-modulet.</p>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-xl">Annullér</Button>
          <Button onClick={save} disabled={saving} className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]" data-testid="finding-followup-save">{saving ? "Gemmer…" : "Gem opfølgning"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function FindingCard({ finding: f, readonly = false, noLink = false, onChanged, badge = null }) {
  const [hist, setHist] = useState(false);
  const [fu, setFu] = useState(false);
  const latest = f.latest_followup;
  const prev = Math.max(0, (f.followup_count || 0) - 1);
  const title = <p className="text-[15px] font-medium text-[#1A1919] mt-1.5 whitespace-pre-wrap" data-testid={`finc-title-${f.id}`}>{f.description || "—"}</p>;
  return (
    <div className="bg-white rounded-2xl border border-[#EBE8E3] p-4 print-avoid" data-testid={`finc-${f.id}`}>
      <div className="flex items-center gap-2 flex-wrap text-xs text-stone-500">
        <span className="font-mono text-stone-400">{f.finding_no}</span>
        <span className={`text-[11px] px-2 py-0.5 rounded-full border ${NEUTRAL}`}>{f.category}</span>
        {f.overdue && f.status !== "Lukket" ? <MStatusBadge overdue deadline={f.due_date} testid={`finc-status-${f.id}`} /> : <span className={`text-[11px] px-2 py-0.5 rounded-full border ${FST_BADGE[f.status] || NEUTRAL}`} data-testid={`finc-status-${f.id}`}>{f.status}</span>}
        <span>Frist {f.due_date ? daDate(f.due_date) : "—"}</span>
        <span>·</span><span>{f.responsible_name || "—"}</span>
        {badge}
      </div>
      {noLink ? title : <Link to={`/kvalitet/audit/fund/${f.id}`} className="block hover:text-[#A83232]">{title}</Link>}
      <p className="text-[13px] text-stone-600 mt-0.5" data-testid={`finc-origin-${f.id}`}>{f.origin?.line}{f.area_name ? ` · ${f.area_name}` : ""}</p>
      {f.status === "Lukket" && f.resolution && <p className="text-sm text-stone-700 mt-2"><span className="text-[#5f6b4a] font-medium">Løsning: </span>{f.resolution}</p>}
      {latest ? (
        <div className="mt-3 rounded-xl px-3 py-2 border bg-[#FBFAF8] border-[#F0EDE8]" data-testid={`finc-latest-${f.id}`}>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-[#806B61]">Seneste opfølgning <span className="font-normal normal-case tracking-normal text-stone-500">· {daDate(latest.at)} · {latest.by_name || "—"}</span></p>
          <div className="mt-0.5"><FEvent f={latest} /></div>
        </div>
      ) : (!readonly && <p className="mt-2 text-sm text-stone-500 italic" data-testid={`finc-latest-${f.id}`}>Ingen opfølgning endnu</p>)}
      {(f.actions || []).length > 0 && (
        <div className="mt-3" data-testid={`finc-actions-${f.id}`}>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-[#806B61] mb-1.5">Tilknyttede handlinger ({f.actions.length})</p>
          <div className="space-y-2">{f.actions.map((a) => <FollowupCardCompact key={a.id} action={a} noLink={noLink} />)}</div>
        </div>
      )}
      <div className="flex items-center justify-between gap-2 mt-2.5 text-xs">
        {prev > 0 ? <button onClick={() => setHist((h) => !h)} className="text-[#806B61] hover:text-[#A83232] flex items-center gap-1" data-testid={`finc-history-${f.id}`}>{hist ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />} {prev} tidligere {prev === 1 ? "opfølgning" : "opfølgninger"}</button> : <span />}
        {!readonly && <button onClick={() => setFu(true)} className="inline-flex items-center gap-1 rounded-full border border-[#EBE8E3] px-3 py-1 text-[#5C4A42] hover:border-[#A83232] hover:text-[#A83232] transition-colors" data-testid={`finc-add-${f.id}`}><Plus className="h-3.5 w-3.5" /> Opfølgning</button>}
      </div>
      {hist && <div className="mt-2 border-l-2 border-[#EBE8E3] pl-3 space-y-2">{(f.followups || []).slice(0, -1).reverse().map((e) => <div key={e.id}><p className="text-[11px] text-stone-400">{daDate(e.at)} · {e.by_name || "—"}</p><FEvent f={e} /></div>)}</div>}
      {fu && <FindingFollowupDialog finding={f} onClose={() => setFu(false)} onSaved={onChanged} />}
    </div>
  );
}

const GST = { "Åben": "bg-[#F5F4F2] text-stone-600", "I gang": "bg-[#F4EFEC] text-[#806B61]", "Opfyldt": "bg-[#E7EADF] text-[#5f6b4a]", "Lukket": "bg-[#E7EADF] text-[#5f6b4a]" };

function GoalBar({ g, latest }) {
  const b = g.baseline_value, t = g.target_value, v = latest?.value;
  if (b == null || t == null || b === t) return null;
  const pct = v == null ? 0 : Math.max(0, Math.min(100, Math.round(((v - b) / (t - b)) * 100)));
  const u = g.unit || "";
  const Col = ({ label, val, cls = "" }) => <div className={cls}><p className="text-[11px] text-stone-500">{label}</p><p className="text-lg font-semibold text-[#1A1919]">{val == null ? "—" : `${val}${u}`}</p></div>;
  return (
    <div className="mt-3" data-testid={`goalc-bar-${g.id}`}>
      <div className="grid grid-cols-3"><Col label="Udgangspunkt" val={b} /><Col label="Nu" val={v} cls="text-center" /><Col label="Mål" val={t} cls="text-right" /></div>
      <div className="h-1.5 rounded-full bg-[#EBE8E3] mt-1.5 overflow-hidden"><div className="h-full rounded-full bg-[#1A1919]" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

export function GoalCard({ goal: g, readonly = false, noLink = false, onEvaluate, actions = [], badge = null, children }) {
  const [hist, setHist] = useState(false);
  const evs = g.evaluations || [];
  const latest = g.latest_evaluation || evs[evs.length - 1] || null;
  const prev = Math.max(0, evs.length - 1);
  const title = <p className="text-[15px] font-medium text-[#1A1919] mt-1.5 whitespace-pre-wrap">{g.title}</p>;
  return (
    <div className="bg-white rounded-2xl border border-[#EBE8E3] p-4 print-avoid" data-testid={`goalc-${g.id}`}>
      <div className="flex items-center gap-2 flex-wrap text-xs text-stone-500">
        <span className="font-mono text-stone-400">{g.goal_no}</span>
        <span className={`text-[11px] px-2 py-0.5 rounded-full ${GST[g.status] || "bg-stone-100"}`}>{g.status}</span>
        {g.category && <span className="text-[11px] px-2 py-0.5 rounded-full border border-[#EBE8E3]">{g.category}</span>}
        {g.overdue && <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#A83232] text-white flex items-center gap-1"><Flag className="h-3 w-3" /> Overskredet</span>}
        <span className={g.overdue ? "text-[#A83232] font-medium" : ""}>Frist {g.deadline ? daDate(g.deadline) : "—"}</span>
        <span>·</span><span>{g.responsible_name || "—"}</span>
        {badge}
      </div>
      {noLink ? title : <Link to={`/maal/${g.id}`} className="block hover:text-[#A83232]">{title}</Link>}
      {g.baseline && <p className="text-[13px] text-stone-600 mt-0.5">Udgangspunkt: {g.baseline}</p>}
      <GoalBar g={g} latest={latest} />
      {latest ? (
        <div className="mt-3 rounded-xl px-3 py-2 border bg-[#FBFAF8] border-[#F0EDE8]" data-testid={`goalc-latest-${g.id}`}>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-[#806B61]">Seneste evaluering <span className="font-normal normal-case tracking-normal text-stone-500">· {daDate(latest.at)} · {latest.by_name || "—"}{latest.meeting_title ? ` · ${latest.meeting_title}` : ""}</span></p>
          {latest.text && <p className="text-sm text-stone-700 whitespace-pre-wrap mt-0.5">{latest.text}</p>}
          {latest.value != null && <p className="text-xs text-stone-600 mt-0.5">Værdi: {latest.value}{g.unit || ""}</p>}
        </div>
      ) : (!readonly && <p className="mt-2 text-sm text-stone-500 italic">Ingen evaluering endnu</p>)}
      {actions.length > 0 && (
        <div className="mt-3" data-testid={`goalc-actions-${g.id}`}>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-[#806B61] mb-1.5">Tilknyttede handlinger ({actions.length})</p>
          <div className="space-y-2">{actions.map((a) => <FollowupCardCompact key={a.id} action={a} noLink={noLink} />)}</div>
        </div>
      )}
      <div className="flex items-center justify-between gap-2 mt-2.5 text-xs">
        {prev > 0 ? <button onClick={() => setHist((h) => !h)} className="text-[#806B61] hover:text-[#A83232] flex items-center gap-1" data-testid={`goalc-history-${g.id}`}>{hist ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />} {prev} tidligere {prev === 1 ? "evaluering" : "evalueringer"}</button> : <span />}
        {!readonly && onEvaluate && <button onClick={onEvaluate} className="inline-flex items-center gap-1 rounded-full border border-[#EBE8E3] px-3 py-1 text-[#5C4A42] hover:border-[#A83232] hover:text-[#A83232] transition-colors" data-testid={`goalc-eval-${g.id}`}><Plus className="h-3.5 w-3.5" /> Evaluering</button>}
      </div>
      {hist && <div className="mt-2 border-l-2 border-[#EBE8E3] pl-3 space-y-2">{evs.slice(0, -1).reverse().map((e) => <div key={e.id}><p className="text-[11px] text-stone-400">{daDate(e.at)} · {e.by_name || "—"}{e.meeting_title ? ` · ${e.meeting_title}` : ""}</p>{e.text && <p className="text-sm text-stone-700 whitespace-pre-wrap">{e.text}</p>}{e.value != null && <p className="text-xs text-stone-600">Værdi: {e.value}{g.unit || ""}</p>}</div>)}</div>}
      {children}
    </div>
  );
}
